import { useState } from "react";
import { useSelector } from "react-redux";
import { Menu } from "lucide-react";

import {
  Shell,
  Main,
  TopBar,
  TopLeft,
  Hamburger,
  Titles,
  Title,
  Subtitle,
} from "../../../components/PartnerDashboard/PartnerLayout/styles";
import AdminSidebar from "./AdminSidebar";
import { ADMIN_SECTIONS, AdminSectionKey } from "./sections";
import UsersPage from "./pages/UsersPage";
import BookingsPage from "./pages/BookingsPage";
import PayoutsPage from "./pages/PayoutsPage";
import GymsPage from "./pages/GymsPage";
import InvitesPage from "./pages/InvitesPage";
import EventsPage from "./pages/EventsPage";
import DiscountsPage from "./pages/DiscountsPage";
import NotificationsPage from "./pages/NotificationsPage";
import UsagePage from "./pages/UsagePage";
import ReferralsPage from "./pages/ReferralsPage";

interface RootState {
  appData: { allBusiness: any[] };
}

const COLLAPSED_KEY = "jr.adminSidebarCollapsed";
const SECTION_KEY = "jr.adminSection";

const readStored = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

const initialSection = (): AdminSectionKey => {
  const stored = readStored(SECTION_KEY);
  return ADMIN_SECTIONS.some((s) => s.key === stored) ? (stored as AdminSectionKey) : "users";
};

const AdminDashboard = () => {
  const [active, setActive] = useState<AdminSectionKey>(initialSection);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readStored(COLLAPSED_KEY) === "1");
  const { allBusiness } = useSelector((state: RootState) => state.appData);

  const section = ADMIN_SECTIONS.find((s) => s.key === active)!;

  const select = (key: AdminSectionKey) => {
    setActive(key);
    writeStored(SECTION_KEY, key);
  };

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      writeStored(COLLAPSED_KEY, c ? "0" : "1");
      return !c;
    });
  };

  return (
    <Shell style={{ fontFamily: "'Poppins', sans-serif" }}>
      <AdminSidebar
        active={active}
        onSelect={select}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />
      <Main $collapsed={collapsed}>
        <TopBar>
          <TopLeft>
            <Hamburger onClick={() => setDrawerOpen(true)} aria-label="Open navigation">
              <Menu size={20} />
            </Hamburger>
            <Titles>
              <Title>{section.label}</Title>
              <Subtitle>{section.subtitle}</Subtitle>
            </Titles>
          </TopLeft>
        </TopBar>

        {active === "users" && <UsersPage />}
        {active === "usage" && <UsagePage />}
        {active === "referrals" && <ReferralsPage />}
        {active === "bookings" && <BookingsPage />}
        {active === "payouts" && <PayoutsPage />}
        {active === "gyms" && <GymsPage />}
        {active === "invites" && <InvitesPage />}
        {active === "events" && <EventsPage />}
        {active === "discounts" && <DiscountsPage />}
        {active === "notifications" && <NotificationsPage allBusiness={allBusiness} />}
      </Main>
    </Shell>
  );
};

export default AdminDashboard;
